/**
 * Forgiving search for the desktop site: Cyrillic and Latin spellings match
 * each other ("efiopiya" = "эфиопия" = "Ethiopia"), small typos are tolerated
 * ("эфеопия", "колумбя"). Everything is compared in one simplified Latin form.
 */
const CYR: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "j", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sh", ъ: "", ы: "i", ь: "", э: "e", ю: "yu", я: "ya",
  ў: "o", қ: "k", ғ: "g", ҳ: "h",
};

export function normalize(s: string): string {
  let out = "";
  for (const ch of s.toLowerCase()) out += CYR[ch] ?? ch;
  return out
    .replace(/['’ʻʼ`‘]/g, "")
    .replace(/th/g, "f")
    .replace(/ph/g, "f")
    .replace(/x/g, "h")
    .replace(/q/g, "k")
    .replace(/w/g, "v")
    .replace(/c(?=[eiy])/g, "s")
    .replace(/c/g, "k")
    .replace(/iy(?=[aeiou])/g, "i")
    .replace(/yo/g, "e")
    .replace(/(.)\1+/g, "$1")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function distance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
}

/** How well one query word matches the text's words (0 = not at all). */
function wordScore(q: string, words: string[]): number {
  let best = 0;
  for (const w of words) {
    if (w === q) return 4;
    if (w.startsWith(q)) best = Math.max(best, 3);
    else if (q.length >= 3 && w.includes(q)) best = Math.max(best, 2);
    else if (q.length >= 4) {
      const head = w.slice(0, q.length);
      const allowed = q.length >= 7 ? 2 : 1;
      if (distance(q, head) <= allowed || distance(q, w) <= allowed) best = Math.max(best, 1);
    }
  }
  return best;
}

/** Score of a text for a query: every query word must match something; 0 = no match. */
export function matchScore(query: string, ...texts: Array<string | null | undefined>): number {
  const qs = normalize(query).split(" ").filter(Boolean);
  if (!qs.length) return 0;
  const words = normalize(texts.filter(Boolean).join(" ")).split(" ").filter(Boolean);
  let total = 0;
  for (const q of qs) {
    const s = wordScore(q, words);
    if (!s) return 0;
    total += s;
  }
  return total;
}
